// Local placement hints (GDD §7), derived from content capabilities exactly like the sim's command rules. They only
// shape UX (hint labels, dimmed options): the sim validates every command and answers with commandRejected.

import {
  BUILDING_KEYS,
  BUILDINGS,
  CAPTURE,
  UNITS,
  keyOf,
  type BuildingEffect,
  type BuildingKey,
  type UnitDef,
  type UnitKey,
} from '@taxwar/content'
import { RejectReason, type BuildingTypeId, type MapCityView, type PlayerId } from '@taxwar/net'
import type { CityLiveState } from '../match/contracts'

type DeploySite = 'air' | 'sea'
type EffectOf<K extends BuildingEffect['kind']> = Extract<BuildingEffect, { readonly kind: K }>

/** Unit domain a building lets its city deploy (Airbase → air, Port → sea). */
const EFFECT_DEPLOY_SITE: { readonly [K in BuildingEffect['kind']]: DeploySite | null } = {
  revenue: null,
  deploy_air: 'air',
  deploy_sea: 'sea',
  sam_site: null,
  fortify: null,
  approval: null,
}

const EFFECT_COASTAL_ONLY: { readonly [K in BuildingEffect['kind']]: (effect: EffectOf<K>) => boolean } = {
  revenue: () => false,
  deploy_air: () => false,
  deploy_sea: (effect) => effect.coastalOnly,
  sam_site: () => false,
  fortify: () => false,
  approval: () => false,
}

function coastalOnly(key: BuildingKey): boolean {
  const effect = BUILDINGS[key].effect
  const rule = EFFECT_COASTAL_ONLY[effect.kind] as (effect: BuildingEffect) => boolean
  return rule(effect)
}

function hasDeploySite(state: CityLiveState, site: DeploySite): boolean {
  for (const typeId of state.buildings) {
    if (EFFECT_DEPLOY_SITE[BUILDINGS[buildingKeyOf(typeId)].effect.kind] === site) return true
  }
  return false
}

/** GDD §7 deploy sites: land anywhere owned; air at the capital or an Airbase; naval at a Port. */
const DEPLOY_SITE_CHECKS: { readonly [K in UnitDef['deployRequires']]: (city: MapCityView, state: CityLiveState) => boolean } = {
  none: () => true,
  airbase_or_capital: (city, state) => city.capital || hasDeploySite(state, 'air'),
  port: (_city, state) => hasDeploySite(state, 'sea'),
}

export function buildingKeyOf(typeId: BuildingTypeId): BuildingKey {
  return keyOf(BUILDING_KEYS, typeId)
}

/** Building slots of a city (content CAPTURE: the capital has more). */
export function buildingSlots(city: MapCityView): number {
  return city.capital ? CAPTURE.capitalSlots : CAPTURE.citySlots
}

/** Why a deploy here would be rejected as far as the client can tell, or null if it looks valid. */
export function deployObjection(unit: UnitKey, city: MapCityView, state: CityLiveState, you: PlayerId): RejectReason | null {
  if (state.owner !== you) return RejectReason.notYourCity
  if (state.fallen) return RejectReason.cityFallen
  if (!DEPLOY_SITE_CHECKS[UNITS[unit].deployRequires](city, state)) return RejectReason.deploySiteMissing
  return null
}

/** Why a build here would be rejected as far as the client can tell, or null if it looks valid. */
export function buildObjection(building: BuildingKey, city: MapCityView, state: CityLiveState, you: PlayerId): RejectReason | null {
  if (state.owner !== you) return RejectReason.notYourCity
  if (state.fallen) return RejectReason.cityFallen
  if (coastalOnly(building) && !city.coastal) return RejectReason.notCoastal
  if (state.buildings.length >= buildingSlots(city)) return RejectReason.noFreeSlot
  return null
}
